import { useEffect, useMemo, useState } from "react";
import { IndicatorGaugeGroup, IndicatorGaugeValues } from "./indicator-gauges";
import {
  IndicatorContribution,
  IndicatorHistoryPoint,
  IndicatorName,
  selectCurrentContribution,
  selectRecentIndicatorHistoryFromRecords,
} from "./indicator-history";
import {
  apiUrl,
  CarId,
  CourseLapRange,
  CoursePoint,
  CoursePresentationAsset,
  DriverPseudoIndicatorRecord,
  FiveHtRecord,
  FiveHtSide,
  isDriverPseudoIndicatorRecord,
  isFiveHtRecord,
  latestDriverPseudoIndicatorRecord,
  latestFiveHtRecord,
  PlayState,
  PSEUDO_INDICATORS,
  PseudoIndicator,
  RaceTraceRecord,
  StoryEvidenceItem,
  StoryEvidenceLedger,
} from "./protocol";

interface CarPresentation {
  number: string;
  driver: string;
  color: string;
  goalRaceTimeMs: number;
}

export const CAR_PRESENTATIONS: Record<CarId, CarPresentation> = {
  car1: { number: "01", driver: "岩佐 歩夢", color: "#ff3048", goalRaceTimeMs: 3_649_975 },
  car6: { number: "06", driver: "太田 格之進", color: "#39e8ff", goalRaceTimeMs: 3_650_204 },
  car14: { number: "14", driver: "福住 仁嶺", color: "#f4c542", goalRaceTimeMs: 3_649_722 },
};

const STATUS_ITEMS = [
  ["前景化重み", "動作中"],
  ["物理出力への介入", "無効"],
  ["学習則", "接続済み"],
  ["パラメータ更新", "0件"],
  ["出自", "古典同定 warm start"],
] as const;

// The SFgo throttle channel uses a 0-120 telemetry scale.  Keep the replay
// model on that native scale and normalize only the human-facing percentage.
const THROTTLE_TELEMETRY_FULL_SCALE = 120;
const UNAVAILABLE_LABEL = "未準備";

type NumericState = Record<PseudoIndicator, number>;
type PseudoIndicatorName = PseudoIndicator | "5-HT";
type RelativePseudoIndicator = {
  current: number;
  band: "low" | "middle_low" | "middle_high" | "high";
  interpretation: string;
};

interface PresentationDistribution {
  q1: number;
  median: number;
  q3: number;
  sample_count: number;
  scope: "formation_to_goal";
}

type PresentationDistributions = Record<PseudoIndicatorName, PresentationDistribution>;

interface RacePresentationWindow {
  schema_version: "fixed-point-race-presentation-window/v1";
  car_id: CarId;
  at_ms: number;
  history_window_ms: 2_000;
  course: CoursePresentationAsset["course"] & {
    current_lap: {
      range: CourseLapRange;
      source_record_count: number;
      display_sampling: { method: string; stride: number };
      trace_records: RaceTraceRecord[];
    };
  };
  trace: {
    coverage: { start_race_time_ms: number; end_race_time_ms: number; record_count: number };
    current_record: RaceTraceRecord | null;
    interpolation_records: RaceTraceRecord[];
    recent_records: RaceTraceRecord[];
  };
  five_ht: { current_record: FiveHtRecord | null; recent_records: FiveHtRecord[] };
  driver_indicators: {
    common_scale_id: string;
    common_scale_sha256: string;
    current_record: DriverPseudoIndicatorRecord | null;
    recent_records: DriverPseudoIndicatorRecord[];
  };
  distributions: { driver: PresentationDistributions; vehicle: PresentationDistributions };
}

export interface FiveHtDisplayState {
  reserve: number;
  activity: number;
  daAttenuation: number;
  naFastAttenuation: number;
  naSlow: number;
}

export interface FiveHtDisplayRecord {
  raceTimeMs: number;
  carId: CarId;
  causalId: string;
  rawDriverIndicatorCausalId?: string;
  intervention: false;
  driver: FiveHtDisplayState;
  vehicle: FiveHtDisplayState;
}

export interface RacePresentationContextSnapshot {
  carId: CarId;
  raceTimeMs: number;
  seekGeneration: number;
  pseudoIndicators: {
    driver: Record<PseudoIndicator | "5-HT", number>;
    vehicle: Record<PseudoIndicator | "5-HT", number>;
  };
  pseudoIndicatorRelative: {
    driver: Record<PseudoIndicatorName, RelativePseudoIndicator>;
    vehicle: Record<PseudoIndicatorName, RelativePseudoIndicator>;
  };
  raceEvents: { event_id: string; label_ja: string }[];
  activeStory: { evidence_id: string; text_ja: string }[];
}

function relativePseudoIndicator(
  current: number,
  distribution: PresentationDistribution,
): RelativePseudoIndicator {
  const { q1, median, q3 } = distribution;
  const band = current <= q1 ? "low" : current <= median ? "middle_low" : current <= q3 ? "middle_high" : "high";
  return {
    current,
    band,
    interpretation: band === "low" ? "この車両のレース全体で低い"
      : band === "middle_low" ? "この車両のレース全体でやや低い"
        : band === "middle_high" ? "この車両のレース全体でやや高い"
          : "この車両のレース全体で高い",
  };
}

interface FiveHtSeriesState {
  carId: CarId;
  records: FiveHtRecord[];
}

interface DriverIndicatorSeriesState {
  carId: CarId;
  records: DriverPseudoIndicatorRecord[];
}

interface DisplayRecord {
  raceTimeMs: number;
  trajectoryX: number;
  trajectoryY: number;
  steering: number;
  throttle: number;
  brake: number;
  gear: number;
  vehicle: NumericState;
}

export function RuntimeStatus() {
  return (
    <section className="runtime-status" aria-label="固定点ランタイム状態">
      {STATUS_ITEMS.map(([label, value]) => (
        <div key={label}><span>{label}</span><strong>{value}</strong></div>
      ))}
    </section>
  );
}

function numeric(record: RaceTraceRecord, key: keyof RaceTraceRecord): number {
  return Number(record[key]);
}

function interpolateTrace(records: RaceTraceRecord[], raceTimeMs: number): DisplayRecord | null {
  if (records.length === 0) return null;
  let low = 0;
  let high = records.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (records[middle].race_time_ms <= raceTimeMs) low = middle;
    else high = middle - 1;
  }
  const before = records[low];
  const after = records[Math.min(low + 1, records.length - 1)];
  const span = after.race_time_ms - before.race_time_ms;
  const ratio = raceTimeMs <= before.race_time_ms || span <= 0 ? 0 : Math.min(1, (raceTimeMs - before.race_time_ms) / span);
  const mix = (key: keyof RaceTraceRecord) => numeric(before, key) + (numeric(after, key) - numeric(before, key)) * ratio;
  const mixVehicleIndicators = () => Object.fromEntries(PSEUDO_INDICATORS.map(name => {
    const key = `vehicle_${name}` as keyof RaceTraceRecord;
    return [name, mix(key)];
  })) as NumericState;
  return {
    raceTimeMs,
    trajectoryX: mix("trajectory_x"),
    trajectoryY: mix("trajectory_y"),
    steering: mix("steering"),
    throttle: mix("throttle"),
    brake: mix("brake"),
    gear: before.gear,
    vehicle: mixVehicleIndicators(),
  };
}

export function selectFiveHtDisplay(
  series: FiveHtSeriesState | null,
  carId: CarId,
  raceTimeMs: number,
): FiveHtDisplayRecord | null {
  if (!series || series.carId !== carId || series.records.length === 0) return null;
  const records = series.records;
  const record = latestFiveHtRecord(records, carId, raceTimeMs);
  if (!record || raceTimeMs > records[records.length - 1].race_time_ms) return null;
  const side = (name: FiveHtSide): FiveHtDisplayState => name === "driver" ? {
    reserve: record.driver_5HT_reserve,
    activity: record.driver_5HT_activity,
    daAttenuation: record.driver_DA_attenuation,
    naFastAttenuation: record.driver_NA_fast_attenuation,
    naSlow: record.driver_NA_slow,
  } : {
    reserve: record.vehicle_5HT_reserve,
    activity: record.vehicle_5HT_activity,
    daAttenuation: record.vehicle_DA_attenuation,
    naFastAttenuation: record.vehicle_NA_fast_attenuation,
    naSlow: record.vehicle_NA_slow,
  };
  return {
    raceTimeMs: record.race_time_ms,
    carId,
    causalId: record.causal_id,
    rawDriverIndicatorCausalId: record.driver_raw_indicator_causal_id,
    intervention: false,
    driver: side("driver"),
    vehicle: side("vehicle"),
  };
}

const PRESENTATION_WINDOW_BUCKET_MS = 1_000;
// One-second buckets retain roughly the latest twelve seconds of navigation.
const PRESENTATION_WINDOW_CACHE_LIMIT = 12;
const presentationWindowCache = new Map<string, RacePresentationWindow>();

function presentationWindowAtMs(carId: CarId, raceTimeMs: number): number {
  const bounded = Math.min(CAR_PRESENTATIONS[carId].goalRaceTimeMs, Math.max(0, raceTimeMs));
  return Math.min(
    CAR_PRESENTATIONS[carId].goalRaceTimeMs,
    Math.ceil(bounded / PRESENTATION_WINDOW_BUCKET_MS) * PRESENTATION_WINDOW_BUCKET_MS,
  );
}

function orderedForCar(records: { car_id: CarId; race_time_ms: number }[], carId: CarId): boolean {
  return records.every((record, index) => record.car_id === carId
    && Number.isInteger(record.race_time_ms)
    && (index === 0 || record.race_time_ms > records[index - 1].race_time_ms));
}

function isTraceRecord(value: unknown, carId: CarId): value is RaceTraceRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<RaceTraceRecord>;
  return record.car_id === carId && Number.isInteger(record.race_time_ms)
    && Number.isInteger(record.gear)
    && ["steering", "throttle", "brake", "trajectory_x", "trajectory_y",
      ...PSEUDO_INDICATORS.map(name => `vehicle_${name}`)]
      .every(key => Number.isFinite(Number(record[key as keyof RaceTraceRecord])));
}

function isDistribution(value: unknown): value is PresentationDistribution {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<PresentationDistribution>;
  return [item.q1, item.median, item.q3].every(Number.isFinite)
    && Number(item.q1) <= Number(item.median) && Number(item.median) <= Number(item.q3)
    && Number.isInteger(item.sample_count) && Number(item.sample_count) > 0
    && item.scope === "formation_to_goal";
}

function isRacePresentationWindow(
  value: unknown, carId: CarId, atMs: number,
): value is RacePresentationWindow {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<RacePresentationWindow>;
  const course = item.course;
  const trace = item.trace;
  const fiveHt = item.five_ht;
  const driver = item.driver_indicators;
  const distributionNames = [...PSEUDO_INDICATORS, "5-HT"] as const;
  if (item.schema_version !== "fixed-point-race-presentation-window/v1"
    || item.car_id !== carId || item.at_ms !== atMs || item.history_window_ms !== 2_000
    || course?.circuit !== "suzuka" || course.closed !== true || !Array.isArray(course.points)
    || course.points.length < 3 || !course.current_lap?.range
    || !Array.isArray(course.current_lap.trace_records)
    || !trace || !Array.isArray(trace.interpolation_records) || !Array.isArray(trace.recent_records)
    || !fiveHt || !Array.isArray(fiveHt.recent_records)
    || !driver || !Array.isArray(driver.recent_records)
    || typeof driver.common_scale_id !== "string"
    || !/^[0-9a-f]{64}$/.test(driver.common_scale_sha256 ?? "")) return false;
  const traces = [...trace.interpolation_records, ...trace.recent_records,
    ...course.current_lap.trace_records];
  if (!traces.every(record => isTraceRecord(record, carId))
    || !orderedForCar(trace.interpolation_records, carId)
    || !orderedForCar(trace.recent_records, carId)
    || !orderedForCar(course.current_lap.trace_records, carId)
    || (trace.current_record !== null && !isTraceRecord(trace.current_record, carId))
    || !fiveHt.recent_records.every(isFiveHtRecord)
    || !orderedForCar(fiveHt.recent_records, carId)
    || (fiveHt.current_record !== null && (!isFiveHtRecord(fiveHt.current_record)
      || fiveHt.current_record.car_id !== carId))
    || !driver.recent_records.every(isDriverPseudoIndicatorRecord)
    || !orderedForCar(driver.recent_records, carId)
    || (driver.current_record !== null && (!isDriverPseudoIndicatorRecord(driver.current_record)
      || driver.current_record.car_id !== carId))) return false;
  const driverRecords = driver.current_record
    ? [...driver.recent_records, driver.current_record] : driver.recent_records;
  if (driverRecords.some(record => record.common_scale_id !== driver.common_scale_id
    || record.common_scale_sha256 !== driver.common_scale_sha256)) return false;
  return distributionNames.every(name => isDistribution(item.distributions?.driver?.[name])
    && isDistribution(item.distributions?.vehicle?.[name]));
}

function cachePresentationWindow(window: RacePresentationWindow): void {
  const key = `${window.car_id}:${window.at_ms}`;
  presentationWindowCache.delete(key);
  presentationWindowCache.set(key, window);
  while (presentationWindowCache.size > PRESENTATION_WINDOW_CACHE_LIMIT) {
    const oldest = presentationWindowCache.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    presentationWindowCache.delete(oldest);
  }
}

function useRacePresentationWindow(
  carId: CarId,
  atMs: number,
  seekGeneration: number,
  enabled: boolean,
): { window: RacePresentationWindow | null; status: "loading" | "ready" | "error"; error: string } {
  const [window, setWindow] = useState<RacePresentationWindow | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setError("");
    if (!enabled) return () => controller.abort();
    const key = `${carId}:${atMs}`;
    const cached = presentationWindowCache.get(key);
    if (cached) {
      setWindow(cached);
      setStatus("ready");
      return () => controller.abort();
    }
    setStatus("loading");
    fetch(apiUrl("/api/race/presentation-window", { car_id: carId, at_ms: atMs }), {
      signal: controller.signal,
    }).then(async response => {
      if (!response.ok) throw new Error(`race presentation window request failed: ${response.status}`);
      const parsed = await response.json() as unknown;
      if (!isRacePresentationWindow(parsed, carId, atMs))
        throw new Error("race presentation window schema differs");
      if (!controller.signal.aborted) {
        cachePresentationWindow(parsed);
        setWindow(parsed);
        setStatus("ready");
      }
    }).catch(reason => {
      if (reason?.name !== "AbortError") {
        setError(String(reason));
        setStatus("error");
      }
    });
    return () => controller.abort();
  }, [atMs, carId, enabled, seekGeneration]);

  return { window, status, error };
}

interface CourseGeometry {
  path: string;
  project: (x: number, y: number) => { x: number; y: number };
}

function buildCourseGeometry(points: CoursePoint[]): CourseGeometry | null {
  if (points.length < 3) return null;
  const xs = points.map(point => point.x);
  const ys = points.map(point => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const width = Math.max(1, maxX - minX);
  const height = Math.max(1, maxY - minY);
  const scale = Math.min(920 / width, 540 / height);
  const offsetX = (1000 - width * scale) / 2;
  const offsetY = (620 - height * scale) / 2;
  const project = (x: number, y: number) => ({
    x: offsetX + (x - minX) * scale,
    y: 620 - (offsetY + (y - minY) * scale),
  });
  const path = points.map((point, index) => {
    const projected = project(point.x, point.y);
    return `${index === 0 ? "M" : "L"}${projected.x.toFixed(1)},${projected.y.toFixed(1)}`;
  }).join(" ") + " Z";
  return { path, project };
}

function traceIndexAtTime(records: RaceTraceRecord[], raceTimeMs: number): number {
  if (records.length === 0) return -1;
  let low = 0;
  let high = records.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (records[middle].race_time_ms <= raceTimeMs) low = middle;
    else high = middle - 1;
  }
  return low;
}

function buildCurrentLapPath(
  records: RaceTraceRecord[],
  raceTimeMs: number,
  geometry: CourseGeometry | null,
): string {
  if (!geometry || records.length === 0) return "";
  const points: CoursePoint[] = records
    .filter(record => record.race_time_ms <= raceTimeMs)
    .map(record => ({ x: numeric(record, "trajectory_x"), y: numeric(record, "trajectory_y") }));
  return points.map((point, index) => {
    const projected = geometry.project(point.x, point.y);
    return `${index === 0 ? "M" : "L"}${projected.x.toFixed(1)},${projected.y.toFixed(1)}`;
  }).join(" ");
}

function traceHistory(
  records: RaceTraceRecord[],
  name: PseudoIndicator,
  raceTimeMs: number,
): IndicatorHistoryPoint[] {
  return selectRecentIndicatorHistoryFromRecords(
    records,
    raceTimeMs,
    record => numeric(record, `vehicle_${name}` as keyof RaceTraceRecord),
  );
}

function fiveHtHistory(
  records: FiveHtRecord[],
  side: FiveHtSide,
  raceTimeMs: number,
): IndicatorHistoryPoint[] {
  return selectRecentIndicatorHistoryFromRecords(
    records,
    raceTimeMs,
    record => side === "driver" ? record.driver_5HT_activity : record.vehicle_5HT_activity,
    record => record.causal_id,
  );
}

function driverHistory(
  records: DriverPseudoIndicatorRecord[],
  name: PseudoIndicator,
  raceTimeMs: number,
): IndicatorHistoryPoint[] {
  return selectRecentIndicatorHistoryFromRecords(
    records,
    raceTimeMs,
    record => record[`driver_${name}` as keyof DriverPseudoIndicatorRecord] as number,
    record => record.causal_id,
  );
}

function driverContribution(
  record: DriverPseudoIndicatorRecord | null,
  name: IndicatorName,
  raceTimeMs: number,
  requireCausalId?: string,
): IndicatorContribution | null {
  if (!record || (requireCausalId !== undefined && record.causal_id !== requireCausalId)) return null;
  const producer = record.driver_indicator_contributors[name];
  return selectCurrentContribution([{
    side: "driver",
    indicator: name,
    raceTimeMs: record.race_time_ms,
    causalId: record.causal_id,
    scores: producer.scores,
    hasDominantOperation: producer.has_dominant_operation,
    leadingOperation: producer.leading_operation,
  }], "driver", name, raceTimeMs);
}

function buildDriverGaugeValues(
  series: DriverIndicatorSeriesState | null,
  current: DriverPseudoIndicatorRecord | null,
  fiveHtSeries: FiveHtSeriesState | null,
  fiveHt: FiveHtDisplayRecord | null,
  raceTimeMs: number,
): IndicatorGaugeValues {
  const records = series?.records ?? [];
  const pseudo = PSEUDO_INDICATORS.reduce((result, name) => {
    result[name] = {
      value: current ? current[`driver_${name}` as keyof DriverPseudoIndicatorRecord] as number : null,
      causalId: current?.causal_id,
      history: driverHistory(records, name, raceTimeMs),
      contribution: driverContribution(current, name, raceTimeMs),
    };
    return result;
  }, {} as Pick<IndicatorGaugeValues, PseudoIndicator>);
  return {
    ...pseudo,
    "5-HT": {
      value: fiveHt?.driver.activity ?? null,
      causalId: fiveHt?.causalId,
      history: fiveHtHistory(fiveHtSeries?.records ?? [], "driver", raceTimeMs),
      contribution: driverContribution(current, "5-HT", raceTimeMs, fiveHt?.rawDriverIndicatorCausalId),
    },
  };
}

function buildVehicleGaugeValues(
  records: RaceTraceRecord[],
  values: NumericState | null,
  fiveHtSeries: FiveHtSeriesState | null,
  fiveHt: FiveHtDisplayRecord | null,
  raceTimeMs: number,
): IndicatorGaugeValues {
  const pseudo = PSEUDO_INDICATORS.reduce((result, name) => {
    result[name] = {
      value: values?.[name] ?? null,
      history: traceHistory(records, name, raceTimeMs),
      contribution: null,
    };
    return result;
  }, {} as Pick<IndicatorGaugeValues, PseudoIndicator>);
  return {
    ...pseudo,
    "5-HT": {
      value: fiveHt?.vehicle.activity ?? null,
      causalId: fiveHt?.causalId,
      history: fiveHtHistory(fiveHtSeries?.records ?? [], "vehicle", raceTimeMs),
      contribution: null,
    },
  };
}

function StoryPanel({ items, raceTimeMs }: { items: StoryEvidenceItem[]; raceTimeMs: number }) {
  const active = items.filter(item => item.enabled && item.start_race_time_ms <= raceTimeMs && item.end_race_time_ms >= raceTimeMs);
  return (
    <section className="story-panel">
      <div className="section-title"><span>RACE STORY</span><small>根拠台帳に一致する記述のみ</small></div>
      {active.length === 0 ? (
        <p className="story-empty">この時刻に表示可能な根拠付きイベントはありません。</p>
      ) : active.map(item => (
        <article className="story-card" key={item.evidence_id} data-evidence-id={item.evidence_id}>
          <span>{item.evidence_class === "public_statement" ? "公開資料" : item.evidence_class === "telemetry_observation" ? "テレメトリ観測" : "推論"}</span>
          <h3>{item.ui_text_ja}</h3>
          <p>{item.claim_ja}</p>
          {item.source.url
            ? <a href={item.source.url} target="_blank" rel="noreferrer">出典を開く ↗</a>
            : <small>出典: {item.source.path}</small>}
        </article>
      ))}
    </section>
  );
}

export function RacePresentation({
  carId,
  raceTimeMs,
  seekGeneration = 0,
  playState = "paused",
  dataFetchEnabled = true,
  onContextChange,
}: {
  carId: CarId;
  raceTimeMs: number;
  seekGeneration?: number;
  playState?: PlayState;
  dataFetchEnabled?: boolean;
  onContextChange?: (snapshot: RacePresentationContextSnapshot | null) => void;
}) {
  const [storyItems, setStoryItems] = useState<StoryEvidenceItem[]>([]);
  const [storyCarId, setStoryCarId] = useState<CarId | null>(null);
  const [storyError, setStoryError] = useState("");
  const profile = CAR_PRESENTATIONS[carId];
  const boundedRaceTimeMs = Math.min(raceTimeMs, profile.goalRaceTimeMs);
  const windowAtMs = presentationWindowAtMs(carId, boundedRaceTimeMs);
  const presentation = useRacePresentationWindow(
    carId, windowAtMs, seekGeneration, dataFetchEnabled,
  );
  const activeWindow = presentation.window?.car_id === carId
    && presentation.window.at_ms === windowAtMs ? presentation.window : null;

  useEffect(() => {
    const controller = new AbortController();
    if (!dataFetchEnabled) return () => controller.abort();
    setStoryItems([]);
    setStoryCarId(null);
    setStoryError("");
    fetch(apiUrl("/api/race/story", { car_id: carId }), {
      cache: "no-store", signal: controller.signal,
    }).then(async storyResponse => {
      if (!storyResponse.ok) throw new Error(`story evidence request failed: ${storyResponse.status}`);
      const ledger = await storyResponse.json() as StoryEvidenceLedger;
      if (!controller.signal.aborted) {
        setStoryItems(ledger.items);
        setStoryCarId(carId);
      }
    }).catch(reason => {
      if (reason?.name !== "AbortError") setStoryError(String(reason));
    });
    return () => controller.abort();
  }, [carId, dataFetchEnabled]);

  const records = activeWindow?.trace.recent_records ?? [];
  const lapRecords = activeWindow?.course.current_lap.trace_records ?? [];
  const fiveHtSeries = useMemo<FiveHtSeriesState | null>(() => activeWindow ? ({
    carId, records: activeWindow.five_ht.recent_records,
  }) : null, [activeWindow, carId]);
  const driverIndicatorSeries = useMemo<DriverIndicatorSeriesState | null>(() => activeWindow ? ({
    carId, records: activeWindow.driver_indicators.recent_records,
  }) : null, [activeWindow, carId]);
  const display = useMemo(() => interpolateTrace(records, Math.min(raceTimeMs, profile.goalRaceTimeMs)), [records, raceTimeMs, profile.goalRaceTimeMs]);
  const recordsBelongToCurrentCar = records.length > 0 && records.every(record => record.car_id === carId);
  const fiveHtDisplay = useMemo(
    () => selectFiveHtDisplay(fiveHtSeries, carId, boundedRaceTimeMs),
    [boundedRaceTimeMs, carId, fiveHtSeries],
  );
  const driverIndicatorDisplay = useMemo(
    () => driverIndicatorSeries
      ? latestDriverPseudoIndicatorRecord(driverIndicatorSeries.records, carId, boundedRaceTimeMs)
      : null,
    [boundedRaceTimeMs, carId, driverIndicatorSeries],
  );
  const basePresentationReady = activeWindow !== null && recordsBelongToCurrentCar
    && fiveHtSeries?.carId === carId && fiveHtDisplay !== null;
  const driverPresentationReady = driverIndicatorSeries?.carId === carId && driverIndicatorDisplay !== null;
  const presentationReady = basePresentationReady && driverPresentationReady;
  const safeDisplay = basePresentationReady ? display : null;
  const courseGeometry = useMemo(
    () => buildCourseGeometry(activeWindow?.course.points ?? []),
    [activeWindow],
  );
  const currentLapRange = activeWindow?.course.current_lap.range ?? null;
  const currentLapPath = useMemo(
    () => buildCurrentLapPath(lapRecords, boundedRaceTimeMs, courseGeometry),
    [boundedRaceTimeMs, courseGeometry, lapRecords],
  );
  const safeCourse = basePresentationReady ? courseGeometry : null;
  const safeLapPath = basePresentationReady ? currentLapPath : "";
  const marker = safeDisplay && safeCourse ? safeCourse.project(safeDisplay.trajectoryX, safeDisplay.trajectoryY) : null;
  const throttlePercent = safeDisplay
    ? Math.max(0, Math.min(100, safeDisplay.throttle / THROTTLE_TELEMETRY_FULL_SCALE * 100))
    : null;
  const phase = raceTimeMs <= 212_000 ? "FORMATION" : raceTimeMs >= profile.goalRaceTimeMs ? "GOAL / HOLD" : "RACE";
  const presentationStatus = presentation.status === "error" ? "error"
    : presentation.status === "ready" && presentationReady ? "ready" : "loading";
  const driverGaugeValues = useMemo(
    () => buildDriverGaugeValues(
      driverIndicatorSeries,
      driverIndicatorDisplay,
      fiveHtSeries,
      fiveHtDisplay,
      boundedRaceTimeMs,
    ),
    [boundedRaceTimeMs, driverIndicatorDisplay, driverIndicatorSeries, fiveHtDisplay, fiveHtSeries],
  );
  const vehicleGaugeValues = useMemo(
    () => buildVehicleGaugeValues(
      records,
      safeDisplay?.vehicle ?? null,
      fiveHtSeries,
      fiveHtDisplay,
      boundedRaceTimeMs,
    ),
    [boundedRaceTimeMs, fiveHtDisplay, fiveHtSeries, records, safeDisplay?.vehicle],
  );
  const pseudoIndicatorRelative = useMemo(() => {
    const names = [...PSEUDO_INDICATORS, "5-HT"] as const;
    if (!activeWindow) return null;
    const current = (source: IndicatorGaugeValues, name: PseudoIndicatorName) => source[name].value;
    if (names.some(name => current(driverGaugeValues, name) === null
      || current(vehicleGaugeValues, name) === null)) return null;
    const side = (gauges: IndicatorGaugeValues, distributions: PresentationDistributions) =>
      Object.fromEntries(names.map(name => [name, relativePseudoIndicator(
        Number(current(gauges, name)), distributions[name],
      )])) as Record<PseudoIndicatorName, RelativePseudoIndicator>;
    return {
      driver: side(driverGaugeValues, activeWindow.distributions.driver),
      vehicle: side(vehicleGaugeValues, activeWindow.distributions.vehicle),
    };
  }, [activeWindow, driverGaugeValues, vehicleGaugeValues]);
  const contextSnapshot = useMemo<RacePresentationContextSnapshot | null>(() => {
    const names = [...PSEUDO_INDICATORS, "5-HT"] as const;
    const driverReady = names.every(name => driverGaugeValues[name].value !== null);
    const vehicleReady = names.every(name => vehicleGaugeValues[name].value !== null);
    if (!presentationReady || !driverReady || !vehicleReady || !pseudoIndicatorRelative
      || storyCarId !== carId) return null;
    const values = (source: IndicatorGaugeValues) => Object.fromEntries(
      names.map(name => [name, Number(source[name].value)]),
    ) as Record<PseudoIndicator | "5-HT", number>;
    const active = storyItems.filter(item => item.enabled
      && item.start_race_time_ms <= boundedRaceTimeMs && item.end_race_time_ms >= boundedRaceTimeMs);
    return {
      carId,
      raceTimeMs: boundedRaceTimeMs,
      seekGeneration,
      pseudoIndicators: { driver: values(driverGaugeValues), vehicle: values(vehicleGaugeValues) },
      pseudoIndicatorRelative,
      raceEvents: active.map(item => ({ event_id: item.evidence_id, label_ja: item.ui_text_ja })),
      activeStory: active.map(item => ({ evidence_id: item.evidence_id, text_ja: item.claim_ja })),
    };
  }, [boundedRaceTimeMs, carId, driverGaugeValues, presentationReady, pseudoIndicatorRelative,
    seekGeneration, storyCarId, storyItems, vehicleGaugeValues]);

  useEffect(() => {
    onContextChange?.(contextSnapshot);
  }, [contextSnapshot, onContextChange]);

  return (
    <section
      className="race-presentation"
      data-testid="race-presentation"
      data-car-id={carId}
      data-seek-generation={seekGeneration}
      data-play-state={playState}
      data-presentation-window-at-ms={windowAtMs}
      data-five-ht-status={presentation.status}
      data-five-ht-causal-id={fiveHtDisplay?.causalId ?? ""}
      data-driver-indicator-status={presentation.status}
      data-driver-indicator-causal-id={driverIndicatorDisplay?.causal_id ?? ""}
    >
      <header className="driver-strip" style={{ "--car-color": profile.color } as React.CSSProperties}>
        <strong>{profile.number}</strong>
        <div><span>DRIVER</span><h2>{profile.driver}</h2></div>
        <div><span>RACE PHASE</span><h3>{phase}</h3></div>
        <div><span>DATA STATUS</span><h3 className={`data-${presentationStatus}`}>{presentationStatus === "ready" ? "FULL-RACE READY" : presentationStatus.toUpperCase()}</h3></div>
      </header>
      {presentation.error && <p className="error" role="alert">走行表示データを読み込めません: {presentation.error}</p>}
      {storyError && <p className="error" role="alert">レースストーリーを読み込めません: {storyError}</p>}
      <div className="presentation-grid">
        <article className="track-panel">
          <div className="section-title"><span>SUZUKA COURSE / CURRENT LAP GPS</span><small>鈴鹿基準中心線と現在周回の記録値</small></div>
          <svg viewBox="0 0 1000 620" role="img" aria-label={`${profile.driver}の鈴鹿基準コース、現在周回の走行線、現在位置`}>
            <path className="course-shadow" d={safeCourse?.path ?? ""} />
            <path className="course-surface" d={safeCourse?.path ?? ""} />
            <path className="trajectory-shadow" d={safeLapPath} />
            <path className="trajectory-line" d={safeLapPath} style={{ "--car-color": profile.color } as React.CSSProperties} />
            {marker && <g className="car-marker" transform={`translate(${marker.x} ${marker.y})`} style={{ "--car-color": profile.color } as React.CSSProperties}>
              <circle r="22" /><circle r="7" /><text y="-31">{profile.number}</text>
            </g>}
          </svg>
          <div className="track-legend" aria-label="コース表示の凡例">
            <span><i className="legend-course" />鈴鹿の基準中心線</span>
            <span><i className="legend-trajectory" style={{ "--car-color": profile.color } as React.CSSProperties} />記録GPS、現在周回の現在時刻まで</span>
            <strong>LAP {currentLapRange?.lap_counter ?? "–"}</strong>
          </div>
          <div className="signal-readout">
            <div><span>GEAR</span><strong>{safeDisplay?.gear ?? "–"}</strong></div>
            <div><span>THROTTLE</span><strong>{throttlePercent === null ? "–" : `${throttlePercent.toFixed(1)}%`}</strong></div>
            <div><span>BRAKE</span><strong>{safeDisplay ? safeDisplay.brake.toFixed(1) : "–"}</strong></div>
            <div><span>STEERING</span><strong>{safeDisplay ? safeDisplay.steering.toFixed(1) : "–"}</strong></div>
          </div>
        </article>
        <aside className="indicator-panel">
          {safeDisplay ? <>
            <IndicatorGaugeGroup
              title="DRIVER MODEL"
              values={driverGaugeValues}
              fiveHtDetail={fiveHtDisplay?.driver ?? null}
            />
            <IndicatorGaugeGroup
              title="VEHICLE MODEL"
              values={vehicleGaugeValues}
              fiveHtDetail={fiveHtDisplay?.vehicle ?? null}
            />
          </> : <p className="loading-copy">{UNAVAILABLE_LABEL} — フルレース系列を読み込み中…</p>}
        </aside>
      </div>
      <StoryPanel items={storyCarId === carId ? storyItems : []} raceTimeMs={raceTimeMs} />
    </section>
  );
}
